import { AccordionDetails, Stack, Typography } from '@mui/material';
import { type ReactElement, useCallback, useMemo } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  FlatAccordion,
  FlatAccordionSummary,
} from 'src/components/FlatAccordion/flat-accordion';
import type { ConversationAttachment } from 'src/graphql/entities/conversation';
import { useMoveAttachmentToSiteFiles } from 'src/graphql/entities/files-upload';
import { mapConversationAttachmentRows } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/attachment-utils';
import { ConversationAttachmentRow } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/conversation-attachment-row';
import { useConversationAttachmentsVisibility } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/use-conversation-attachments-visibility';
import { SectionHeader } from 'src/pages/Conversations/SidePanel/section-header';

interface ConversationAttachmentsProps {
  attachments: ConversationAttachment[];
  conversationId: string;
  siteAddressId?: string;
  userId?: string;
}

export const ConversationAttachments = ({
  attachments,
  conversationId,
  siteAddressId,
  userId,
}: ConversationAttachmentsProps): ReactElement | null => {
  const { changeConversationAttachmentsVisibility, currentConversationAttachmentsVisibility } =
    useConversationAttachmentsVisibility();
  const { moveToSiteFiles } = useMoveAttachmentToSiteFiles();
  const canAddToSiteFiles = Boolean(siteAddressId);

  const rows = useMemo(() => mapConversationAttachmentRows(attachments), [attachments]);

  const onChangeConversationAttachmentsVisibility = useCallback(
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      changeConversationAttachmentsVisibility(isExpanded);
    },
    [changeConversationAttachmentsVisibility],
  );

  if (attachments.length === 0) {
    return null;
  }

  return (
    <FlatAccordion
      data-testid="conversation-attachments-section"
      disableGutters
      elevation={0}
      expanded={currentConversationAttachmentsVisibility}
      onChange={onChangeConversationAttachmentsVisibility}
    >
      <FlatAccordionSummary
        aria-controls={`conversation-attachments-content-${conversationId}`}
        data-testid="conversation-attachments-toggle"
        expandIcon={<DynamicIcon name="expand_more" />}
        id={`conversation-attachments-header-${conversationId}`}
      >
        <Stack alignItems="center" direction="row" gap={0.5} width="100%">
          <SectionHeader title="Attachments" />
          <Typography
            color="text.secondary"
            data-testid="conversation-attachments-count"
            fontSize="0.75rem"
          >
            ({rows.length})
          </Typography>
        </Stack>
      </FlatAccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Stack data-testid="conversation-attachments-list">
          {rows.map((attachment) => (
            <ConversationAttachmentRow
              key={attachment.id}
              attachment={attachment}
              canDragToSiteFiles={canAddToSiteFiles}
              onAddToSiteFiles={
                canAddToSiteFiles
                  ? () =>
                      moveToSiteFiles({
                        attachment: attachment.siteFilePayload,
                        addressId: siteAddressId,
                        uploadedByUserId: userId,
                      })
                  : undefined
              }
            />
          ))}
        </Stack>
      </AccordionDetails>
    </FlatAccordion>
  );
};
